import { useEffect, useMemo, useRef } from 'react';
import {
  Alert,
  Box,
  Stack,
  Typography,
  TextField,
  MenuItem,
  FormControl,
  ListItemIcon,
  ListItemText,
  FormControlLabel,
  Checkbox,
  FormHelperText,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  SubmitHandler,
  useForm,
  Controller,
  FormProvider,
  Resolver,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirm } from 'components/ConfirmDialog';
import { Map } from 'features/mapping/components/Map';
import { MapType } from 'features/mapping/mapTypePolicy';
import { Location } from 'features/locations/location.types';
import { useCacheState } from 'state/useCacheState';
import { RelatedWildfire } from 'features/wildfires/wildfireGeoEventData.types';
import { RelatedFlooding } from 'features/flooding/flooding.types';
import { getLocationIcon } from 'features/mapping/icons';
import { RichTextEditor } from 'components/RichTextEditor';
import { InternalNotesInput } from 'components/InternalNotesInput';
import { FormLinks } from 'components/FormLinks';
import { useIosKeyboardScroller } from 'hooks/useIosKeyboardScroller';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { GeoEventFormTabs } from 'features/geoEvents/components/GeoEventFormTabs';
import { InternalFormSection } from 'features/geoEvents/components/InternalFormSection';
import { useGeoEventAutocomplete } from 'features/geoEvents/hooks/useGeoEventAutocomplete';
import { enqueueSnackbar } from 'notistack';
import { FormWithoutEnter } from 'components/FormWithoutEnter';
import { FormHours } from 'features/locations/components/FormHours';
import { useLocationV2Enabled } from 'features/locations/hooks/useLocationV2Enabled';
import { getLocationTypeTransKey } from 'features/locations/location.constants';
import { useUserRegionsQuery } from 'hooks/useUserRegionsQuery';
import {
  isCreateLocationDisabled,
  isCreateLocationProvisional,
  isEditDisabledForPermission,
  isEditLocationProvisional,
  usePermissions,
} from 'hooks/usePermissions';
import { useProvisionalSubmissionMutations } from 'features/provisionalSubmissions/hooks/useProvisionalSubmissionMutations';
import { useProvisionalGeoEventActions } from 'features/provisionalSubmissions/hooks/useProvisionalGeoEventActions';
import {
  CreateGeoEventProvisionalSubmissionResponse,
  EditLocationProvisionalSubmissionResponse,
  ProvisionalSubmissionResponse,
} from 'features/provisionalSubmissions/provisionalSubmissions.types';
import { getDateFormatted } from 'shared/dates';
import { isLocationEditor } from 'shared/utils';
import { useAuthState } from 'state/useAuthState';
import { useSyncInitialLocationToFormState } from 'features/geoEvents/hooks/useSyncInitialLocationToFormState';
import { useFormBlocker } from 'hooks/useFormBlocker';
import { useGeoEventEditMarker } from 'features/geoEvents/hooks/useGeoEventEditMarker';
import { useMarkerEditLock } from 'features/geoEvents/hooks/useMarkerEditLock';
import { useMarkerMoveConfirmation } from 'features/geoEvents/hooks/useMarkerMoveConfirmation';
import { getIncidentRemountKey } from 'features/geoEvents/utils/getIncidentRemountKey';
import { iconFootprint } from 'features/mapping/components/MarkerEditConnectorLine';
import { MarkerEditControls } from 'features/mapping/components/MarkerEditControls';
import { MarkerEditGhost } from 'features/mapping/components/MarkerEditGhost';
import {
  getRedundancyMessage,
  getViewGeoEventLink,
} from 'features/geoEvents/geoEvent.utils';
import { MapFormLayout } from 'components/MapFormLayout';
import { useDefaultMapCenter } from 'hooks/useDefaultMapCenter';
import { useLatLngFromQueryParams } from 'hooks/useLatLngFromQueryParams';
import { useInitialMapZoom } from 'hooks/useInitialMapZoom';
import { HttpStatusCode, isAxiosError } from 'axios';
import { attemptAsync } from 'es-toolkit/util';
import { AutocompleteWithBackdrop } from 'components/AutocompleteWithBackdrop';
import { Loader } from 'components/Loader';
import { useAllGeoEvents } from 'features/geoEvents/hooks/useAllGeoEvents';
import { LocationsLayer } from 'features/mapping/components/layers/LocationsLayer';
import {
  getInitialValues,
  getSubmitButtonKey,
  locationFormDataToServerData,
  postLocation,
  updateLocation,
} from './LocationForm.utils';
import { LocationFormValues, LocationType } from './LocationForm.types';
import { LocationReviewActions } from './LocationReviewActions';
import { LocationSubmitActions } from './LocationSubmitActions';
import { useStyles } from './LocationForm.styles';
import {
  legacyLocationTypeOptions,
  locationTypeOptions,
  statusOptions,
} from './LocationForm.constants';
import { getValidationSchema } from './LocationForm.validation';
import { GeoEventTypes, LocationTypes } from '../../../../constants';

type LocationFormProps = {
  location?: Location;
  parentGeoEventId?: number;
  parentGeoEventType?: 'wildfire' | 'flooding';
  submissionUnderReview?:
    | CreateGeoEventProvisionalSubmissionResponse
    | EditLocationProvisionalSubmissionResponse;
};

type LocationMarkerProps = {
  location: Partial<Location>;
  onChange: (location: { lat: number; lng: number }) => void;
  locked?: boolean;
};

const getLocationTypeValue = (
  showLocationV2: boolean,
  locationType: LocationType | null,
): LocationType | null => {
  if (!showLocationV2) return locationType;
  // Handles backward compatibility
  if (locationType === LocationTypes.food) {
    return LocationTypes.meals;
  }
  if (locationType === LocationTypes.animalShelter) {
    return LocationTypes.smallAnimalShelter;
  }
  return locationType;
};

const LocationMarker = (props: LocationMarkerProps): JSX.Element => {
  const { location, onChange, locked = false } = props;
  const { markerProps } = useGeoEventEditMarker({
    geoEventType: GeoEventTypes.location,
    onChange,
    disabled: locked,
  });

  return (
    <Map.LocationMarker
      {...markerProps}
      location={location}
      draggable={!locked}
      scale={location.data?.locationType ? 1.5 : 1}
    />
  );
};

export const LocationForm = (props: LocationFormProps): JSX.Element => {
  const {
    location,
    parentGeoEventId,
    parentGeoEventType,
    submissionUnderReview,
  } = props;
  const isReviewing = Boolean(submissionUnderReview);
  const reviewSubmittedData =
    submissionUnderReview?.submittedData.geoEventType === 'location'
      ? submissionUnderReview.submittedData
      : undefined;
  const { lat: initialLatitude, lng: initialLongitude } =
    useLatLngFromQueryParams();
  const initialZoom = useInitialMapZoom();
  const { classes } = useStyles();
  const { t } = useTranslation();
  const defaultMapCenter = useDefaultMapCenter({
    geoEvent: location,
    initialLat: reviewSubmittedData?.lat ?? initialLatitude,
    initialLng: reviewSubmittedData?.lng ?? initialLongitude,
  });
  const { setCacheState } = useCacheState();
  const queryClient = useQueryClient();
  const showLocationV2 = useLocationV2Enabled();
  const isInternalTabEnabled = useReleaseFlag('internal_tab_on_incidents');
  const confirm = useConfirm();
  const { locations } = useAllGeoEvents();

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  useIosKeyboardScroller(scrollContainerRef);

  const { regions: regionOptions, isLoading: isLoadingRegions } =
    useUserRegionsQuery();

  const createLocationMutation = useMutation({ mutationFn: postLocation });

  const updateLocationMutation = useMutation({ mutationFn: updateLocation });

  const { submitProvisionalGeoEventCreate, submitProvisionalGeoEventEdit } =
    useProvisionalSubmissionMutations();

  // requiresIncidentAssociation depends on the watched lat/lng and those aren't known until after the form is
  // created, and they can change. resolver reads the latest value from a ref so validation stays current as they move
  const requiresIncidentAssociationRef = useRef(false);
  const resolver = useMemo<Resolver<LocationFormValues>>(
    () => (values, context, options) =>
      yupResolver(
        getValidationSchema(t, requiresIncidentAssociationRef.current),
      )(values, context, options),
    [t],
  );

  const initialValues: LocationFormValues = useMemo(
    () =>
      getInitialValues({
        location,
        latitude: initialLatitude,
        longitude: initialLongitude,
        reviewSubmittedData,
      }),
    [initialLatitude, initialLongitude, location, reviewSubmittedData],
  );

  const formMethods = useForm<LocationFormValues>({
    resolver,
    defaultValues: initialValues,
    values: initialValues, // will update form values when initialValues change (location updates)
    mode: 'onBlur',
  });

  const { control, formState, handleSubmit, setValue, watch } = formMethods;

  const { unblockedNavigate } = useFormBlocker({
    shouldBlockNavigation: Boolean(formState.isDirty),
  });

  const { approveSubmission, rejectSubmission, isRejecting } =
    useProvisionalGeoEventActions({
      reviewSubmission: submissionUnderReview,
      geoEvent: isReviewing && location ? { id: location.id } : undefined,
      navigate: unblockedNavigate,
    });

  const geoEventType = GeoEventTypes.location;
  const { handleLocationChange } = useSyncInitialLocationToFormState({
    isLoadingRegions,
    regionOptions,
    geoEventType,
    geoEvent: location,
    setValue,
  });

  const handleSubmitSuccess = (createdLocationId?: number): void => {
    const isUpdate = Boolean(location?.id);

    const message = isUpdate
      ? t('createEditLocation.successMessage.update')
      : t('createEditLocation.successMessage.create');

    enqueueSnackbar(message, { variant: 'success' });

    if (isUpdate) {
      setCacheState(null);
      queryClient.invalidateQueries({
        queryKey: ['reporterNotes', location!.id],
      });
    }

    let route = '/';
    if (isUpdate) {
      route = `/location/${location!.id}`;
    } else if (parentGeoEventId && parentGeoEventType) {
      route = getViewGeoEventLink(parentGeoEventType, parentGeoEventId);
    } else if (createdLocationId) {
      route = `/location/${createdLocationId}`;
    }

    unblockedNavigate(route);
  };

  const showUnknownError = (error: unknown): void => {
    const errorMessage =
      error instanceof Error ? error.message : t('common.unknownError');
    enqueueSnackbar(errorMessage, { variant: 'error' });
  };

  const showProvisionalSubmitError = (error: unknown): void => {
    if (!isAxiosError(error)) {
      showUnknownError(error);
      return;
    }

    const errorStatus = error.response?.status;
    if (errorStatus === HttpStatusCode.Forbidden) {
      enqueueSnackbar(t('createEditLocation.errors.forbidden'), {
        variant: 'error',
      });
    } else if (errorStatus === HttpStatusCode.BadRequest) {
      enqueueSnackbar(t('createEditLocation.errors.badRequest'), {
        variant: 'error',
      });
    } else {
      showUnknownError(error);
    }
  };

  const submitProvisional = async (
    submit: () => Promise<ProvisionalSubmissionResponse | null>,
    successMessage: string,
    route: string,
  ): Promise<void> => {
    const [error, response] = await attemptAsync(submit);

    if (error) {
      showProvisionalSubmitError(error);
      return;
    }

    if (response === null) return;

    enqueueSnackbar(successMessage, { variant: 'success' });
    unblockedNavigate(route);
  };

  const [type, status, lat, lng, parentGeoEvents] = watch([
    'type',
    'status',
    'lat',
    'lng',
    'parentGeoEvents',
  ]);

  const {
    user,
    permissions: { canReportWildfire, canReportFlooding },
  } = useAuthState();
  const { data: permissionsData } = usePermissions(
    location?.id
      ? { geoEventType, geoEventId: location.id }
      : { geoEventType, lat, lng },
  );

  // Location editors can only create in allowed states
  const canReport = canReportWildfire || canReportFlooding;
  const isEditing = Boolean(location);
  const isLocationOnlyEditor = !canReport && isLocationEditor(user);
  const createDisabled =
    !isReviewing &&
    !isEditing &&
    isLocationOnlyEditor &&
    isCreateLocationDisabled(permissionsData);
  const isProvisionalCreate =
    !isReviewing && !isEditing && isCreateLocationProvisional(permissionsData);
  const isProvisionalEdit =
    !isReviewing && isEditing && isEditLocationProvisional(permissionsData);

  const handleSubmitLocation: SubmitHandler<LocationFormValues> = async (
    values,
  ) => {
    const data = locationFormDataToServerData(values);
    if (!data) return;

    // Delete location v2 related fields
    if (!showLocationV2) {
      if ('hours' in (data.data ?? {})) {
        delete (data.data as { hours?: unknown }).hours;
      }
      if ('at_capacity' in (data.data ?? {})) {
        delete (data.data as { at_capacity?: unknown }).at_capacity;
      }
    }

    if (isReviewing) {
      await approveSubmission(data);
      return;
    }

    if (isProvisionalCreate) {
      await submitProvisional(
        () => submitProvisionalGeoEventCreate(data),
        t('createEditLocation.successMessage.provisional'),
        '/',
      );
      return;
    }

    if (isProvisionalEdit && location?.id) {
      await submitProvisional(
        () => submitProvisionalGeoEventEdit(data, location.id),
        t('createEditLocation.successMessage.provisional'),
        `/location/${location.id}`,
      );
      return;
    }

    const [error, result] = location?.id
      ? await attemptAsync(() =>
          updateLocationMutation.mutateAsync({ id: location.id, data }),
        )
      : await attemptAsync(() => createLocationMutation.mutateAsync({ data }));

    if (!error) {
      handleSubmitSuccess(result?.data.id);
      return;
    }

    if (!isAxiosError(error)) {
      showUnknownError(error);
      return;
    }

    const errorStatus = error.response?.status;

    if (errorStatus === HttpStatusCode.Conflict) {
      // We should check for redundant posts (this only affects CIOs)
      // If there are too many posts in the last few minutes, we should warn them
      const shouldRetry = await confirm(getRedundancyMessage(error.response));

      if (shouldRetry) {
        const [retryError, retryResult] = await attemptAsync(() =>
          createLocationMutation.mutateAsync({
            data,
            skipRedundancyCheck: true,
          }),
        );

        if (retryError) {
          showUnknownError(retryError);
          return;
        }

        handleSubmitSuccess(retryResult?.data.id);
      }
    } else if (errorStatus === HttpStatusCode.Forbidden) {
      enqueueSnackbar(t('createEditLocation.errors.forbidden'), {
        variant: 'error',
      });
    } else if (errorStatus === HttpStatusCode.BadRequest) {
      enqueueSnackbar(t('createEditLocation.errors.badRequest'), {
        variant: 'error',
      });
    } else {
      showUnknownError(error);
    }
  };

  // Location editors can only edit locations created by their own organization in allowed states
  const editDisabled =
    !isReviewing &&
    isEditing &&
    isLocationOnlyEditor &&
    isEditDisabledForPermission(permissionsData?.location?.editPermission);

  // When restrictedToLocationTypes is non-empty, only those types may be selected. If not given, all types are allowed
  const { restrictedToLocationTypes = [] } = permissionsData?.location ?? {};
  const isTypeDisabled = (value: LocationType): boolean =>
    restrictedToLocationTypes.length > 0 &&
    !restrictedToLocationTypes.includes(value);

  const { requiresIncidentAssociation = false } =
    permissionsData?.location ?? {};
  requiresIncidentAssociationRef.current = requiresIncidentAssociation;

  const { muiAutocompleteProps: geoEventAutocompleteProps } =
    useGeoEventAutocomplete<RelatedWildfire | RelatedFlooding>({
      enabled: true,
      latLng: initialValues,
      value: parentGeoEvents,
      geoEventTypes: ['wildfire', 'flooding'],
    });

  useEffect(() => {
    // Skip if no parent geoevent ID is defined or if parent geoevents are already selected
    if (!parentGeoEventId || parentGeoEvents.length > 0) {
      return;
    }

    const initialParentGeoEvent = geoEventAutocompleteProps.options.find(
      (geoEvent) => geoEvent.id === parentGeoEventId,
    );

    // Set the initial parentGeoEvents value
    if (initialParentGeoEvent) {
      setValue('parentGeoEvents', [initialParentGeoEvent], {
        shouldDirty: false,
      });
    }
  }, [
    geoEventAutocompleteProps.options,
    parentGeoEventId,
    parentGeoEvents.length,
    setValue,
  ]);

  const markerLock = useMarkerEditLock({
    onCommit: handleLocationChange,
    isEditing: isEditing || isReviewing,
  });

  // The original marker position, shown as a ghost with a connector line when moved
  const originalMarkerLocation = location ?? reviewSubmittedData;

  const { checkbox: markerMoveCheckbox, isBlockingSubmit: markerMoveBlocking } =
    useMarkerMoveConfirmation({
      originalLatLng: originalMarkerLocation
        ? { lat: originalMarkerLocation.lat, lng: originalMarkerLocation.lng }
        : null,
      currentLatLng: { lat, lng },
      isEditing: isEditing || isReviewing,
    });

  const displayLat = markerLock.stagedLatLng?.lat ?? lat;
  const displayLng = markerLock.stagedLatLng?.lng ?? lng;

  const currentLocation = useMemo(() => {
    return {
      data: { locationType: type },
      isActive: status === 'active',
      lat: displayLat,
      lng: displayLng,
    } as Partial<Location>;
  }, [displayLat, displayLng, status, type]);

  const typeOptions = showLocationV2
    ? locationTypeOptions
    : legacyLocationTypeOptions;

  // When location type restrictions apply and the selected type isn't permitted fall back to the first allowed type
  useEffect(() => {
    if (restrictedToLocationTypes.length === 0) return;
    if (type && restrictedToLocationTypes.includes(type)) return;

    const firstAllowedType = typeOptions.find((option) =>
      restrictedToLocationTypes.includes(option.value),
    )?.value;
    if (firstAllowedType) {
      setValue('type', firstAllowedType, { shouldDirty: false });
    }
  }, [restrictedToLocationTypes, type, typeOptions, setValue]);

  const otherLocations = useMemo(
    () => locations.filter((l) => l.id !== location?.id),
    [locations, location?.id],
  );

  if (isLoadingRegions) {
    return <Loader disableBackground />;
  }

  const formFields = (
    <Stack spacing={2}>
      {!isInternalTabEnabled && (
        <Typography variant="h3">
          {t('createEditLocation.sections.location')}
        </Typography>
      )}

      {(isProvisionalCreate || isProvisionalEdit) && (
        <Alert severity="info">
          {t('createEditLocation.provisionalBanner')}
        </Alert>
      )}

      {submissionUnderReview && (
        <Alert severity="info">
          {t(
            location
              ? 'reviewSubmission.submittedByEditLocation'
              : 'reviewSubmission.submittedByCreateLocation',
            {
              firstName: submissionUnderReview.createdBy.firstName,
              lastName: submissionUnderReview.createdBy.lastName,
              date: getDateFormatted(submissionUnderReview.dateCreated, {
                includeYear: true,
                includeTimeZone: true,
              }),
            },
          )}
        </Alert>
      )}

      <Controller
        name="name"
        control={control}
        render={({ field, fieldState }) => {
          const { ref, ...muiFieldProps } = field;
          return (
            <TextField
              id="field-control-name"
              label={t('createEditLocation.inputs.name.label')}
              fullWidth
              {...muiFieldProps}
              inputRef={ref}
              error={Boolean(fieldState.error)}
              helperText={
                fieldState.error?.message ||
                t('createEditLocation.inputs.name.helperText')
              }
              required
              disabled={formState.isSubmitting}
            />
          );
        }}
      />

      <Controller
        name="address"
        control={control}
        render={({ field, fieldState }) => {
          const { ref, ...muiFieldProps } = field;
          return (
            <TextField
              id="field-control-address"
              label={t('createEditLocation.inputs.address.label')}
              fullWidth
              {...muiFieldProps}
              inputRef={ref}
              error={Boolean(fieldState.error)}
              helperText={
                fieldState.error?.message ||
                t('createEditLocation.inputs.address.helperText')
              }
              required
              disabled={formState.isSubmitting}
            />
          );
        }}
      />

      <Controller
        name="type"
        control={control}
        render={({ field, fieldState }) => {
          const { ref, ...muiFieldProps } = field;
          return (
            <TextField
              select
              id="field-control-type"
              label={t('createEditLocation.inputs.type.label')}
              fullWidth
              {...muiFieldProps}
              value={
                getLocationTypeValue(showLocationV2, muiFieldProps.value) || ''
              }
              inputRef={ref}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              required
              disabled={formState.isSubmitting}
              slotProps={{
                select: {
                  renderValue: (option) =>
                    t(
                      getLocationTypeTransKey(
                        option as LocationType,
                        showLocationV2,
                      ),
                    ),
                },
              }}
            >
              {typeOptions.map(({ transKey, value }) => {
                const icon = getLocationIcon(value, showLocationV2);
                return (
                  <MenuItem
                    key={value}
                    value={value}
                    disabled={isTypeDisabled(value)}
                    className={classes.menuItem}
                  >
                    <ListItemIcon className={classes.itemIcon}>
                      <img
                        src={icon.data}
                        alt={icon.name}
                        width={icon.width}
                        height={icon.height}
                      />
                    </ListItemIcon>
                    <ListItemText primary={t(transKey)} />
                  </MenuItem>
                );
              })}
            </TextField>
          );
        }}
      />

      <Controller
        name="parentGeoEvents"
        control={control}
        render={({ field, fieldState }) => {
          return (
            <FormControl fullWidth>
              <AutocompleteWithBackdrop
                {...geoEventAutocompleteProps}
                id="geoEvents"
                value={field.value}
                onChange={(_, value) => {
                  field.onChange(value);
                }}
                onBlur={field.onBlur}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name={field.name}
                    inputRef={field.ref}
                    variant="outlined"
                    label={t('createEditLocation.inputs.incidents.label')}
                    required={requiresIncidentAssociation}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    disabled={formState.isSubmitting}
                  />
                )}
              />
            </FormControl>
          );
        }}
      />

      <Controller
        name="status"
        control={control}
        render={({ field, fieldState }) => {
          const { ref, ...muiFieldProps } = field;
          return (
            <TextField
              select
              id="field-control-status"
              label={t('createEditLocation.inputs.status.label')}
              fullWidth
              {...muiFieldProps}
              inputRef={ref}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              required
              disabled={formState.isSubmitting}
            >
              {statusOptions.map(({ transKey, value }) => {
                return (
                  <MenuItem
                    key={value}
                    value={value}
                    className={classes.menuItem}
                  >
                    {t(transKey)}
                  </MenuItem>
                );
              })}
            </TextField>
          );
        }}
      />

      {!isInternalTabEnabled && (
        <>
          <Box sx={{ mt: 1 }}>
            <Typography variant="h3">
              {t('createEditLocation.sections.internal')}
            </Typography>
          </Box>

          <InternalNotesInput initialValue={initialValues.reporterOnlyNotes} />
        </>
      )}

      {showLocationV2 && (
        <>
          <Controller
            name="atCapacity"
            control={control}
            render={({ field }): JSX.Element => {
              return (
                <FormControlLabel
                  control={
                    <Checkbox
                      name="atCapacity"
                      checked={field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                      id="field-control-atCapacity"
                    />
                  }
                  label={t('createEditLocation.inputs.atCapacity.label')}
                  sx={{ width: 'fit-content' }}
                />
              );
            }}
          />

          <FormHours locationTimeZone={location?.data.timezone} />
        </>
      )}

      <FormLinks />

      <Box sx={{ mt: 1 }}>
        <Controller
          name="information"
          control={control}
          render={({ field, fieldState }) => {
            return (
              <>
                <RichTextEditor
                  id="field-control-information"
                  key={getIncidentRemountKey('information', location)}
                  initialValue={initialValues.information ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  label={t('createEditLocation.sections.information')}
                  data-testid="location-information"
                />
                <FormHelperText
                  error={Boolean(fieldState.error)}
                  sx={{ mx: 1.75 }}
                  data-testid="info-helper-message"
                >
                  {fieldState.error?.message ||
                    t('createEditLocation.inputs.information.helperText')}
                </FormHelperText>
              </>
            );
          }}
        />
      </Box>

      {markerMoveCheckbox}

      <Box sx={{ marginTop: 2 }}>
        {isReviewing ? (
          <LocationReviewActions
            isSubmitting={formState.isSubmitting}
            isRejecting={isRejecting}
            markerMoveBlocking={markerMoveBlocking}
            hasStagedChange={markerLock.hasStagedChange}
            onReject={rejectSubmission}
          />
        ) : (
          <LocationSubmitActions
            label={t(
              getSubmitButtonKey(
                isEditing,
                isProvisionalCreate || isProvisionalEdit,
              ),
            )}
            disabled={
              markerLock.hasStagedChange ||
              markerMoveBlocking ||
              createDisabled ||
              editDisabled
            }
            isSubmitting={formState.isSubmitting}
            createDisabled={createDisabled}
            editDisabled={editDisabled}
          />
        )}
      </Box>
    </Stack>
  );

  return (
    <MapFormLayout ref={scrollContainerRef}>
      <MapFormLayout.MapContent>
        <Map
          center={defaultMapCenter}
          zoom={initialZoom}
          mapType={MapType.GenericForm}
          showSearch
          searchDisabled={markerLock.isLocked}
          searchBarAdornment={<MarkerEditControls lock={markerLock} />}
          cursor={markerLock.isLocked ? undefined : 'crosshair'}
          onLocationChange={markerLock.handleLocationChange}
          searchApi="google"
        >
          <LocationsLayer locations={otherLocations} interactive={false} />
          <LocationMarker
            location={currentLocation}
            onChange={markerLock.handleLocationChange}
            locked={markerLock.isLocked}
          />
          {originalMarkerLocation && (
            <MarkerEditGhost
              markerLock={markerLock}
              original={originalMarkerLocation}
              footprint={iconFootprint(
                getLocationIcon(
                  originalMarkerLocation.data.locationType,
                  showLocationV2,
                ),
                'bottom',
                1.5,
              )}
            >
              <Map.LocationMarker
                location={originalMarkerLocation as Partial<Location>}
                draggable={false}
                opacityOverride={0.5}
                scale={1.5}
              />
            </MarkerEditGhost>
          )}
        </Map>
      </MapFormLayout.MapContent>
      <MapFormLayout.FormContent disablePadding={isInternalTabEnabled}>
        <FormProvider {...formMethods}>
          <FormWithoutEnter
            noValidate
            onSubmit={handleSubmit(handleSubmitLocation)}
          >
            {isInternalTabEnabled ? (
              <GeoEventFormTabs<LocationFormValues>
                idPrefix="location-form"
                internalFieldNames={['newReporterNote']}
                internalContent={
                  <InternalFormSection
                    key={getIncidentRemountKey('internal-form', location)}
                    geoEventId={location?.id}
                    newReporterNoteInitialValue={initialValues.newReporterNote}
                  />
                }
              >
                {formFields}
              </GeoEventFormTabs>
            ) : (
              formFields
            )}
          </FormWithoutEnter>
        </FormProvider>
      </MapFormLayout.FormContent>
    </MapFormLayout>
  );
};
